/**
 * Connectivity campaigns (#283) — client.
 *
 * A campaign is a long-running test in /tests that owns a set of modems' connectivity mode.
 * Start applies the mode, stop clears it; everything the modems emit in between is scoped to
 * the run, so these reads answer "what happened during THIS campaign?" rather than "what is
 * this modem doing?".
 */
import { apiRequest } from './api';
import type { CampaignConfig, CampaignModemState, TestRun } from '@simcheck/shared-types';

export interface CampaignModemLive extends CampaignModemState {
  /** False once the modem stopped reporting our campaign id — it drifted away. */
  stillManaged?: boolean;
  connectivityMode?: string | null;
  connectivityStatus?: any;
  modemModelName?: string | null;
}

export interface CampaignSummary {
  id: string;
  name: string | null;
  status: string;
  mode: 'persistent' | 'hunt' | null;
  schedule: any;
  startedAt: string | null;
  completedAt: string | null;
  endsAt: string | null;
  stoppedAt: string | null;
  stoppedBy: string | null;
  stopReason: string | null;
  stopping: boolean;
  totals: Record<string, number>;
  /** Hunt only: are there still pass windows inside the 48 h the device can see? */
  windowHorizon: { ok: boolean; lastEnd: string | null } | null;
  windowHorizonWarning: { lastEnd: string | null; at: string | null; message: string } | null;
  modems: CampaignModemLive[];
}

export interface CampaignStartResponse {
  type: 'campaign';
  run: TestRun;
  modems: CampaignModemState[];
}

/** Create + start a campaign. Returns 409 with per-modem detail if every modem refused. */
export async function startCampaign(body: {
  organizationId: string;
  name: string;
  campaign: CampaignConfig;
  locationRequirements: any[];
  timeZone?: string;
  testConfigurationId?: string;
}): Promise<CampaignStartResponse> {
  return apiRequest('/test-runs', {
    method: 'POST',
    body: JSON.stringify({
      organizationId: body.organizationId,
      testConfigurationId: body.testConfigurationId,
      flowName: body.name,
      configuration: {
        kind: 'connectivity_campaign',
        name: body.name,
        campaign: body.campaign,
        locationRequirements: body.locationRequirements,
        timeZone: body.timeZone,
      },
    }),
  });
}

/** Stop a running campaign — clears the mode on every modem it owns. */
export async function stopCampaign(runId: string, reason?: string): Promise<{ released: number; failed: number }> {
  return apiRequest(`/test-runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || 'stopped_by_user' }),
  });
}

export async function getCampaign(runId: string): Promise<CampaignSummary> {
  return apiRequest(`/test-runs/${encodeURIComponent(runId)}/campaign`);
}

type Page<T> = { items: T[]; nextCursor?: string | null };

export async function getCampaignEvents(runId: string, opts?: { limit?: number; modemImei?: string }): Promise<Page<any>> {
  const q = new URLSearchParams();
  if (opts?.limit) q.set('limit', String(opts.limit));
  if (opts?.modemImei) q.set('modemImei', opts.modemImei);
  const s = q.toString();
  return apiRequest(`/test-runs/${encodeURIComponent(runId)}/campaign/events${s ? `?${s}` : ''}`);
}

export async function getCampaignScans(runId: string, opts?: { limit?: number }): Promise<Page<any>> {
  const s = opts?.limit ? `?limit=${opts.limit}` : '';
  return apiRequest(`/test-runs/${encodeURIComponent(runId)}/campaign/scans${s}`);
}

export async function getCampaignTests(runId: string, opts?: { limit?: number }): Promise<Page<any>> {
  const s = opts?.limit ? `?limit=${opts.limit}` : '';
  return apiRequest(`/test-runs/${encodeURIComponent(runId)}/campaign/tests${s}`);
}

export async function getCampaignRollups(runId: string, opts?: { limit?: number }): Promise<Page<any>> {
  const s = opts?.limit ? `?limit=${opts.limit}` : '';
  return apiRequest(`/test-runs/${encodeURIComponent(runId)}/campaign/rollups${s}`);
}

export async function getCampaignWindows(runId: string, opts?: { limit?: number }): Promise<Page<any>> {
  const s = opts?.limit ? `?limit=${opts.limit}` : '';
  return apiRequest(`/test-runs/${encodeURIComponent(runId)}/campaign/windows${s}`);
}
